/*
 * File: useResetPassword.ts
 * Author: Austin Robinson
 * Date Created: 9/7/2025
 * Copyright (c) DrivingDoc 2025
 */
import { createMutation } from 'react-query-kit';

import { ErrorResponse, publicApi } from 'api';

/**
 * Parameters required to reset a user's password using a reset token.
 */
export interface ResetPasswordParams {
  /**
   * The password reset token received via email.
   */
  token: string;

  /**
   * The new password to set for the account.
   */
  newPassword: string;
}

/**
 * Resets a user's password using a password reset token.
 * @param params - The password reset parameters (token and new password).
 * @returns A promise that resolves to `true` if the password was reset successfully.
 * @throws {ErrorResponse} If the API request fails.
 */
async function resetPassword({ token, newPassword }: ResetPasswordParams): Promise<boolean> {
  const result = await publicApi.post(`/public/auth/resetPassword`, {
    token,
    newPassword,
  });

  return result.status === 201;
}

/**
 * Custom React Query mutation hook for resetting a user's password using a password reset token.
 * Integrates with `useErrorCollapse` for displaying errors in an MUI Collapse and Alert component.
 * @returns A mutation hook for resetting a user's password.
 * @example
 * ```tsx
 * const { mutate, isLoading, error } = useResetPassword({
 *   onSuccess: () => {
 *     console.log('Password reset successfully');
 *   },
 *   onError: (error) => {
 *     console.error('Password reset failed:', error);
 *   },
 * });
 *
 * const handleSubmit = () => {
 *   mutate({ token: 'abc123', newPassword: 'newPass123' });
 * };
 * ```
 */
const useResetPassword = createMutation<boolean, ResetPasswordParams, Partial<ErrorResponse>>({
  mutationFn: resetPassword,
});

export { useResetPassword };
